import { Mutation } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage, useIntl } from "react-intl";
import useIntlValidationMessages from "~/lib/hooks/useIntlValidationMessages";
import EditVendorMutation from "~/lib/mutations/EditVendor";
import { IEditVendorMutation, IEditVendorMutationVariables } from "~/lib/mutations/__generated__/EditVendor.generated";
import { IVendorFieldsFragment } from "~/lib/queries/fragments/__generated__/VendorFields.generated";
import validate from "~/lib/utils/validate";
import { IPlanTier } from "~/types/types";
import PreviewModal from "~/components/form/PreviewModal";
import { Margin } from "~/components/spacings";
import convertValuesToVariables from "~/components/vendor/form/convertValuesToVariables";
import convertVendorToInitialValues from "~/components/vendor/form/convertVendorToInitialValues";
import { hasMakersContent } from "~/components/vendor/form/hasMakersContent";
import validationSchema from "~/components/vendor/form/validation";
import VendorForm from "~/components/vendor/form/VendorForm";

interface IEditVendorForm {
  vendor: IVendorFieldsFragment;
}

const EditVendorForm: React.FunctionComponent<IEditVendorForm> = ({ vendor }) => {
  const intl = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const initialValues = convertVendorToInitialValues(vendor);
  const intlValidationMessages = useIntlValidationMessages();
  const router = useRouter();

  return (
    <>
      <Margin mb={16}>
        <div>
          <Link href="/vendors/[vendorSlug]" as={`/vendors/${vendor.slug}`} passHref={true} legacyBehavior={true}>
            <a
              css={{
                fontSize: 16,
                color: palette.innovatorBlue,
                ":hover": {
                  textDecoration: "underline !important",
                },
              }}
            >
              ← <FormattedMessage defaultMessage="Leave Editor" id="Vp9Im5" />
            </a>
          </Link>
        </div>
      </Margin>

      <Mutation<IEditVendorMutation, IEditVendorMutationVariables> mutation={EditVendorMutation}>
        {(editVendor) => {
          return (
            <Form
              subscription={{
                submitting: true,
              }}
              initialValues={initialValues}
              keepDirtyOnReinitialize={true}
              validate={validate(validationSchema(intlValidationMessages))}
              onSubmit={(values) => {
                const makersContent = hasMakersContent(values);

                return editVendor({
                  variables: {
                    id: vendor._id,
                    vendor: convertValuesToVariables(values),
                  },
                }).then((mutationResult) => {
                  if (
                    mutationResult &&
                    mutationResult.data &&
                    mutationResult.data.editVendor &&
                    mutationResult.data.editVendor.slug
                  ) {
                    if (makersContent && mutationResult.data.editVendor.plan.tier === IPlanTier.Basic) {
                      setIsModalOpen(true);
                      return;
                    }

                    if (mutationResult.data.editVendor.plan.tier === IPlanTier.Basic) {
                      router.push("/vendors/[vendorSlug]", `/vendors/${mutationResult.data.editVendor.slug}?preview=1`);
                    } else {
                      router.push(
                        "/vendors/[vendorSlug]",
                        `/vendors/${mutationResult.data.editVendor.slug}?preview=1&makers=1`,
                      );
                    }
                  }
                });
              }}
            >
              {({ handleSubmit, submitting, invalid }) => (
                <>
                  <PreviewModal
                    isOpen={isModalOpen && !invalid}
                    onClose={() => setIsModalOpen(false)}
                    header={intl.formatMessage({
                      defaultMessage: "Profile Saved!",
                      id: "U4nXgh",
                    })}
                    text={intl.formatMessage({
                      defaultMessage:
                        "Some of the information you’ve added will only show for accounts upgraded to Makers.",
                      id: "vNBw6T",
                    })}
                    onPreviewBasicClick={() =>
                      router.push("/vendors/[vendorSlug]", `/vendors/${vendor.slug}?preview=1`)
                    }
                    onPreviewMakersClick={() =>
                      router.push("/vendors/[vendorSlug]", `/vendors/${vendor.slug}?preview=1&makers=1`)
                    }
                  />
                  <VendorForm
                    vendor={vendor}
                    submitting={submitting || false}
                    onSubmit={handleSubmit}
                    initialVendorName={String(initialValues.name)}
                  />
                </>
              )}
            </Form>
          );
        }}
      </Mutation>
    </>
  );
};

export default EditVendorForm;
